import { type SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, signOut } from '../lib/api.ts';
import { setToken } from '../lib/auth.ts';
import { useStore } from '../lib/store.ts';
import { refreshPlan } from '../hooks/usePlan.ts';
import { useCountdown } from '../hooks/useCountdown.ts';
import { type AuthResponse, cleanCode, codeErrorMessage, emailLocale } from '../lib/verification.ts';
import { AuthCard, CodeInput, PrimaryButton } from './authUi.tsx';

interface VerifyEmailProps {
  email: string;
  /** The email is verified: continue into the app. */
  onVerified: () => void;
  /** "Use a different email": the session is gone. */
  onSignOut: () => void;
}

interface ResendBody {
  sent?: boolean;
  retry_after?: number;
  error?: string;
}

/**
 * "Check your email" (docs/EMAIL_VERIFICATION_PLAN.md §3): the 6-digit code
 * sent to an email/password account, with resend and sign-out.
 */
export function VerifyEmail({ email, onVerified, onSignOut }: Readonly<VerifyEmailProps>) {
  const { t } = useTranslation();
  const storeSetUser = useStore((s) => s.setUser);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, startCooldown] = useCountdown();
  const opened = useRef(false);

  // Someone verified in another tab or the app: refresh the user and go on.
  const continueVerified = useCallback(async () => {
    await refreshPlan();
    onVerified();
  }, [onVerified]);

  const resend = useCallback(
    async (ifNeeded: boolean) => {
      setError('');
      try {
        const resp = await apiFetch('/auth/verify/resend', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ locale: emailLocale(), if_needed: ifNeeded }),
        });
        const data = (await resp.json().catch(() => ({}))) as ResendBody;
        if (resp.status === 409 && data.error === 'already_verified') {
          await continueVerified();
          return;
        }
        if (typeof data.retry_after === 'number') startCooldown(data.retry_after);
        if (resp.ok) {
          if (data.sent) setNotice(t('auth.verify.sent', { email }));
        } else if (resp.status === 429) {
          // On open the countdown says it all; a tapped resend gets a line too.
          if (!ifNeeded) setError(t('auth.verify.tooSoon'));
        } else if (resp.status !== 401) {
          setError(codeErrorMessage(t, resp.status, data));
        }
      } catch {
        setError(t('auth.cannotReach'));
      }
    },
    [continueVerified, email, startCooldown, t],
  );

  // On open: send a code unless a live one is already on its way.
  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    void resend(true);
  }, [resend]);

  const handleVerify = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      const resp = await apiFetch('/auth/verify/code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        const auth = data as AuthResponse;
        setToken(auth.token);
        storeSetUser(auth.user);
        onVerified();
      } else if (resp.status === 409) {
        await continueVerified();
      } else if (resp.status !== 401) {
        setError(codeErrorMessage(t, resp.status, data));
      }
    } catch {
      setError(t('auth.cannotReach'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    onSignOut();
  };

  const resendLabel = cooldown > 0
    ? t('auth.verify.resendIn', { seconds: cooldown })
    : t('auth.verify.resend');

  return (
    <AuthCard
      title={t('auth.verify.title')}
      subtitle={t('auth.verify.subtitle', { email })}
      onSubmit={handleVerify}
    >
      <CodeInput value={code} onChange={(v) => setCode(cleanCode(v))} autoFocus />

      {notice && !error && (
        <p className="text-(--color-text-secondary) text-sm text-center">{notice}</p>
      )}
      {error && <p role="alert" className="text-red-400 text-sm text-center">{error}</p>}

      <PrimaryButton disabled={loading || code.length !== 6} loading={loading}>
        {loading ? t('auth.verify.verifying') : t('auth.verify.submit')}
      </PrimaryButton>

      <div className="flex flex-col items-center gap-2 text-xs">
        <button
          type="button"
          onClick={() => { void resend(false); }}
          disabled={cooldown > 0}
          className="text-(--color-accent) hover:underline disabled:text-(--color-text-secondary) disabled:no-underline disabled:cursor-not-allowed"
        >
          {resendLabel}
        </button>
        <button
          type="button"
          onClick={() => { void handleSignOut(); }}
          className="text-(--color-text-secondary) hover:text-(--color-text-primary) hover:underline"
        >
          {t('auth.verify.differentEmail')}
        </button>
      </div>
    </AuthCard>
  );
}
